import { Box, Typography } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useNavigate } from 'react-router-dom';

import { logEvent } from '../../../utils/eventLog';
import { formatUpdatedAgo } from '../../../utils/updatedAgo';
import { CARD_SX, DASH } from '../tokens';
import { BENCHMARKS_ROUTE, percentLine, tileHeadline, withoutHiddenBenchmarks } from '../../Benchmarks/benchmarkMonitoring';
import { BENCH } from '../../Benchmarks/tokens';
import type { BenchmarkMonitoring } from '../../Benchmarks/types';
import { GhostButton } from './GhostButton';

export interface BenchmarkMonitoringTileProps {
  data: BenchmarkMonitoring;
  now?: Date;
}

function scoredPercent(scored: number, base: number): number {
  return base === 0 ? 0 : Math.round((scored / base) * 100);
}

export function BenchmarkMonitoringTile({ data, now = new Date() }: BenchmarkMonitoringTileProps) {
  const navigate = useNavigate();
  const headline = tileHeadline(data, now);
  const benchmarks = withoutHiddenBenchmarks(data).benchmarks;
  const openDetails = () => {
    void logEvent('benchmark_monitoring.widget_opened.v1').catch(() => {});
    navigate(BENCHMARKS_ROUTE);
  };
  return (
    <Box
      data-testid="benchmark-monitoring-tile"
      sx={{ ...CARD_SX, display: 'flex', flexDirection: 'column', gap: '20px', boxSizing: 'border-box', width: '100%' }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <Typography component="h2" sx={{ fontSize: '16px', lineHeight: '24px', fontWeight: 700, color: DASH.ink, m: 0 }}>
          Benchmark Monitoring
        </Typography>
        {data.refreshedAt && (
          <Typography component="p" data-testid="tile-refreshed" sx={{ fontSize: '12px', lineHeight: '16px', fontWeight: 500, color: DASH.muted, m: 0 }}>
            {formatUpdatedAgo(data.refreshedAt, now)}
          </Typography>
        )}
      </Box>
      {headline ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {headline.number !== undefined && (
            <Typography
              component="span"
              data-testid="tile-headline-number"
              sx={{ fontSize: '64px', lineHeight: '64px', fontWeight: 800, letterSpacing: '-0.03em', color: DASH.ink }}
            >
              {headline.number}
            </Typography>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography component="span" sx={{ fontSize: '16px', lineHeight: '24px', fontWeight: 600, color: DASH.ink }}>
              {headline.line}
            </Typography>
            <Typography component="span" sx={{ fontSize: '16px', lineHeight: '24px', fontWeight: 500, color: DASH.muted }}>
              {headline.date}
            </Typography>
          </Box>
        </Box>
      ) : (
        <Typography component="p" sx={{ fontSize: '16px', lineHeight: '24px', fontWeight: 500, color: DASH.muted, m: 0 }}>
          No benchmark window is scheduled this school year.
        </Typography>
      )}
      {benchmarks.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {benchmarks.map((row) => (
            <Box key={row.key} data-testid="tile-benchmark-row" sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Typography component="span" sx={{ width: 140, flexShrink: 0, fontSize: '16px', lineHeight: '24px', fontWeight: 600, color: DASH.ink }}>
                {row.label}
              </Typography>
              <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <Typography component="span" sx={{ fontSize: '12px', lineHeight: '16px', fontWeight: 700, color: DASH.ink, whiteSpace: 'nowrap' }}>
                  {percentLine(row.counts)}
                </Typography>
                <Box sx={{ width: '100%', height: 12, borderRadius: '4px', bgcolor: BENCH.tile.track, overflow: 'hidden' }}>
                  <Box
                    data-testid="tile-bar-fill"
                    sx={{ height: '100%', bgcolor: BENCH.tile.fill }}
                    style={{ width: `${scoredPercent(row.counts.scored, row.counts.base)}%` }}
                  />
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      )}
      <Box sx={{ borderTop: `1px solid ${DASH.rowBorder}`, pt: '16px', display: 'flex', justifyContent: 'flex-end' }}>
        <GhostButton label="See details" onClick={openDetails} endIcon={<ChevronRightIcon sx={{ fontSize: 20 }} />} />
      </Box>
    </Box>
  );
}
